import { apiHelpers } from './normalizedApiClient';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { API_ENDPOINTS } from '../config/api';

/**
 * Servicio para el módulo de Mantenimientos - Backend API
 * 
 * Este servicio maneja todas las operaciones relacionadas con mantenimientos,
 * comunicándose directamente con la nueva API backend en lugar de Supabase.
 */

/**
 * Obtiene todos los mantenimientos con filtros
 * @param {Object} filtros - Filtros de búsqueda
 * @returns {Promise<Array>} Lista de mantenimientos
 */
export const getMantenimientos = async (filtros = {}) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log('🔧 [MantenimientosService] Obteniendo lista con filtros:', filtros);
    
    // Construir query params
    const params = new URLSearchParams();
    if (filtros.sucursal_id) params.append('sucursal_id', filtros.sucursal_id);
    if (filtros.patente) params.append('patente', filtros.patente);
    if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
    if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
    if (filtros.mecanico_id) params.append('mecanico_id', filtros.mecanico_id);
    if (filtros.cliente_nombre) params.append('cliente_nombre', filtros.cliente_nombre);
    
    const url = `${API_ENDPOINTS.MANTENIMIENTOS.BASE}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiHelpers.generic.get(url);
    
    if (response.data) {
      const mantenimientosData = Array.isArray(response.data) ? response.data : [];
      //console.log(`✅ [MantenimientosService] ${mantenimientosData.length} mantenimientos obtenidos`);
      return mantenimientosData;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo mantenimientos:', error);
    throw error;
  }
};

/**
 * Crea un nuevo mantenimiento
 * @param {Object} mantenimientoData - Datos del mantenimiento
 * @returns {Promise<Object>} Mantenimiento creado
 */
export const createMantenimiento = async (mantenimientoData) => {
  try {
    //console.log('🚗 [MantenimientosService] Creando registro');
    //console.log('📝 [MantenimientosService] Creando mantenimiento:', mantenimientoData);
    
    const response = await apiHelpers.generic.post(API_ENDPOINTS.MANTENIMIENTOS.BASE, mantenimientoData);
    
    if (response.data) {
      const mantenimientoCreado = response.data;
      //console.log(`✅ [MantenimientosService] Mantenimiento creado con ID: ${mantenimientoCreado.id}`);
      return mantenimientoCreado;
    }
    
    throw new Error('No se recibieron datos del mantenimiento creado');
  } catch (error) {
    console.error('❌ [MantenimientosService] Error creando mantenimiento:', error);
    throw error;
  }
};

/**
 * Actualiza un mantenimiento existente
 * @param {number} id - ID del mantenimiento
 * @param {Object} mantenimientoData - Datos actualizados del mantenimiento
 * @returns {Promise<Object>} Mantenimiento actualizado
 */
export const updateMantenimiento = async (id, mantenimientoData) => {
  try {
    //console.log('🚗 [MantenimientosService] Actualizando registro');
    //console.log(`📝 [MantenimientosService] Actualizando mantenimiento ID: ${id}`);
    
    const response = await apiHelpers.generic.put(API_ENDPOINTS.MANTENIMIENTOS.BY_ID(id), mantenimientoData);
    
    if (response.data) {
      const mantenimientoActualizado = response.data;
      //console.log(`✅ [MantenimientosService] Mantenimiento ${id} actualizado exitosamente`);
      return mantenimientoActualizado;
    }
    
    throw new Error('No se recibieron datos del mantenimiento actualizado');
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error actualizando mantenimiento ${id}:`, error);
    throw error;
  }
};

/**
 * Elimina un mantenimiento
 * @param {number} id - ID del mantenimiento
 * @returns {Promise<boolean>} True si se eliminó correctamente
 */
export const deleteMantenimiento = async (id) => {
  try {
    //console.log('🚗 [MantenimientosService] Eliminando registro');
    //console.log(`🗑️ [MantenimientosService] Eliminando mantenimiento ID: ${id}`);
    
    await apiHelpers.generic.delete(API_ENDPOINTS.MANTENIMIENTOS.BY_ID(id));
    
    //console.log(`✅ [MantenimientosService] Mantenimiento ${id} eliminado exitosamente`);
    return true;
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error eliminando mantenimiento ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene un mantenimiento por su ID
 * @param {number} id - ID del mantenimiento
 * @returns {Promise<Object>} Mantenimiento encontrado
 */
export const getMantenimientoById = async (id) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo mantenimiento ID: ${id}`);
    
    const response = await apiHelpers.generic.get(API_ENDPOINTS.MANTENIMIENTOS.BY_ID(id));
    
    if (response.data) {
      const mantenimientoData = response.data;
      //console.log(`✅ [MantenimientosService] Mantenimiento ${id} obtenido exitosamente`);
      return mantenimientoData;
    }
    
    return null;
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error obteniendo mantenimiento ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene mantenimientos por patente de vehículo
 * @param {string} patente - Patente del vehículo
 * @returns {Promise<Array>} Lista de mantenimientos del vehículo
 */
export const getMantenimientosByVehiculo = async (patente) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo mantenimientos para vehículo: ${patente}`);
    
    const response = await apiHelpers.generic.get(`${API_ENDPOINTS.MANTENIMIENTOS.BASE}/vehiculo/${encodeURIComponent(patente)}`);
    
    if (response.data) {
      const mantenimientosData = response.data;
      //console.log(`✅ [MantenimientosService] ${mantenimientosData.length} mantenimientos obtenidos para vehículo ${patente}`);
      return Array.isArray(mantenimientosData) ? mantenimientosData : [];
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error obteniendo mantenimientos para vehículo ${patente}:`, error);
    throw error;
  }
};

// === FUNCIONES ESPECÍFICAS PARA EL HOOK useFinalizarMantenimiento ===

/**
 * Obtiene servicios disponibles para mantenimientos
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} Lista de servicios disponibles
 */
export const getServiciosDisponibles = async (sucursalId = null) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo servicios disponibles para sucursal: ${sucursalId}`);
    
    // Usar el servicio de servicios existente
    const { getServicios } = await import('./serviciosApiService');
    return await getServicios(sucursalId);
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo servicios disponibles:', error);
    throw error;
  }
};

/**
 * Obtiene repuestos disponibles para mantenimientos
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} Lista de repuestos disponibles
 */
export const getRepuestosDisponibles = async (sucursalId = null) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo repuestos disponibles para sucursal: ${sucursalId}`);
    
    // Usar el servicio de repuestos existente
    const { getRepuestos } = await import('./repuestosApiService');
    return await getRepuestos(sucursalId, { incluir_inactivos: false });
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo repuestos disponibles:', error);
    throw error;
  }
};

/**
 * Obtiene múltiples configuraciones del sistema
 * @param {Array<string>} claves - Array de claves de configuración
 * @param {number} empresaId - ID de la empresa (opcional)
 * @returns {Promise<Object>} Objeto con las configuraciones
 */
export const getConfiguraciones = async (claves, empresaId = null) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo configuraciones:`, claves);
    
    // Construir query params
    const params = new URLSearchParams();
    claves.forEach(clave => params.append('claves[]', clave));
    if (empresaId) params.append('empresa_id', empresaId);
    
    const response = await apiHelpers.generic.get(`${API_ENDPOINTS.CONFIGURACIONES.BASE}?${params.toString()}`);
    
    if (response.data) {
      const configuracionesData = response.data;
      //console.log(`✅ [MantenimientosService] Configuraciones obtenidas exitosamente`);
      return configuracionesData;
    }
    
    return {};
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo configuraciones:', error);
    // Retornar configuraciones por defecto en caso de error
    return {
      iva_porcentaje: '19',
      garantia_dias_default: '90',
      garantia_kilometros_default: '5000'
    };
  }
};

/**
 * Obtiene toda la configuración del taller para PDFs
 * @param {number} empresaId - ID de la empresa (opcional)
 * @returns {Promise<Object>} Configuración del taller
 */
export const getConfiguracionTaller = async (empresaId = null) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo configuración del taller para empresa: ${empresaId}`);
    
    const claves = [
      'nombre_empresa',
      'direccion_empresa',
      'telefono_empresa',
      'email_empresa',
      'logo_empresa_base64',
      'iva_porcentaje',
      'garantia_dias_default',
      'garantia_kilometros_default'
    ];
    
    return await getConfiguraciones(claves, empresaId);
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo configuración del taller:', error);
    // Retornar configuración por defecto en caso de error
    return {
      nombre_empresa: 'Revvly',
      direccion_empresa: '',
      telefono_empresa: '',
      email_empresa: '',
      logo_empresa_base64: '',
      iva_porcentaje: '19',
      garantia_dias_default: '90',
      garantia_kilometros_default: '5000'
    };
  }
};

/**
 * Crea un mantenimiento completo con todos sus componentes
 * @param {Object} mantenimientoData - Datos del mantenimiento
 * @returns {Promise<Object>} Mantenimiento creado
 */
export const createMantenimientoCompleto = async (mantenimientoData) => {
  try {
    //console.log('🚗 [MantenimientosService] Creando registro');
    //console.log('📝 [MantenimientosService] Creando mantenimiento completo:', mantenimientoData);
    
    const response = await apiHelpers.generic.post(API_ENDPOINTS.MANTENIMIENTOS.BASE, mantenimientoData);
    
    if (response.data) {
      const mantenimientoCreado = response.data;
      //console.log(`✅ [MantenimientosService] Mantenimiento completo creado con ID: ${mantenimientoCreado.id}`);
      return mantenimientoCreado;
    }
    
    throw new Error('No se recibieron datos del mantenimiento creado');
  } catch (error) {
    console.error('❌ [MantenimientosService] Error creando mantenimiento completo:', error);
    throw error;
  }
};

/**
 * Guarda el PDF base64 en el registro de mantenimiento
 * @param {number} mantenimientoId - ID del mantenimiento
 * @param {string} pdfBase64 - PDF en formato base64
 * @returns {Promise<void>}
 */
export const guardarPDFBase64 = async (mantenimientoId, pdfBase64) => {
  try {
    console.log('🚗 [MantenimientosService] Procesando operación');
    console.log(`📝 [MantenimientosService] Guardando PDF base64 para mantenimiento: ${mantenimientoId}`);
    console.log(`📊 [MantenimientosService] Tamaño del PDF base64: ${pdfBase64?.length || 0} caracteres`);
    
    const response = await apiHelpers.generic.post(API_ENDPOINTS.MANTENIMIENTOS.PDF(mantenimientoId), {
      pdf_base64: pdfBase64
    });
    
    console.log(`✅ [MantenimientosService] PDF base64 guardado exitosamente para mantenimiento ${mantenimientoId}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error guardando PDF base64 para mantenimiento ${mantenimientoId}:`, error);
    throw error;
  }
};

/**
 * Obtiene un mantenimiento por ID de cita
 * @param {number} citaId - ID de la cita
 * @returns {Promise<Object|null>} Mantenimiento asociado a la cita
 */
export const getMantenimientoPorCita = async (citaId) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo mantenimiento por cita ID: ${citaId}`);
    
    const response = await apiHelpers.generic.get(API_ENDPOINTS.MANTENIMIENTOS.BY_CITA(citaId));
    
    if (response.data) {
      const mantenimientoData = response.data;
      //console.log(`✅ [MantenimientosService] Mantenimiento encontrado para cita ${citaId}`);
      return mantenimientoData;
    }
    
    //console.log(`⚠️ [MantenimientosService] No se encontró mantenimiento para cita ${citaId}`);
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`⚠️ [MantenimientosService] No existe mantenimiento para cita ${citaId}`);
      return null;
    }
    console.error(`❌ [MantenimientosService] Error obteniendo mantenimiento por cita ${citaId}:`, error);
    throw error;
  }
};

/**
 * Obtiene los mecánicos disponibles de una sucursal
 * @param {Object} filtros - Filtros requeridos
 * @param {number} filtros.sucursal_id - ID de la sucursal (requerido)
 * @returns {Promise<Array>} Lista de mecánicos
 */
export const getMecanicosDisponibles = async (filtros = {}) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    if (!filtros.sucursal_id) {
      console.warn('🔧 [MantenimientosService] getMecanicosDisponibles: sucursal_id es requerido');
      return [];
    }

    //console.log('🔧 [MantenimientosService] Obteniendo mecánicos para sucursal:', filtros.sucursal_id);
    
    const { getMecanicos } = await import('./mecanicosApiService');
    const mecanicos = await getMecanicos(filtros.sucursal_id);
    
    //console.log(`✅ [MantenimientosService] ${mecanicos.length} mecánicos obtenidos`);
    return mecanicos;
  } catch (error) {
    console.error('❌ [MantenimientosService] Error obteniendo mecánicos:', error);
    throw error;
  }
};

/**
 * Obtiene los servicios de una cita específica
 * @param {number} citaId - ID de la cita
 * @returns {Promise<Array>} Servicios de la cita
 */
export const getServiciosPorCita = async (citaId) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo servicios para cita: ${citaId}`);
    
    const { getServiciosSeleccionados } = await import('./citasApiService');
    const servicios = await getServiciosSeleccionados(citaId);
    
    //console.log(`✅ [MantenimientosService] ${servicios?.length || 0} servicios obtenidos para cita ${citaId}`);
    return servicios || [];
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error obteniendo servicios para cita ${citaId}:`, error);
    throw error;
  }
};

/**
 * Obtiene el historial completo de un vehículo (mantenimientos + citas futuras)
 * @param {string} patente - Patente del vehículo
 * @returns {Promise<Object>} Objeto con mantenimientos y citas futuras
 */
export const getHistorialVehiculo = async (patente) => {
  try {
    //console.log('🚗 [MantenimientosService] Obteniendo datos');
    //console.log(`🔍 [MantenimientosService] Obteniendo historial completo para vehículo: ${patente}`);
    
    const response = await apiHelpers.generic.get(API_ENDPOINTS.MANTENIMIENTOS.HISTORIAL(encodeURIComponent(patente)));
    
    if (response.data) {
      const historialData = response.data;
      /*console.log(`✅ [MantenimientosService] Historial obtenido para vehículo ${patente}:`, {
        mantenimientos: historialData.mantenimientos?.length || 0,
        citasFuturas: historialData.citasFuturas?.length || 0
      });*/
      
      return {
        mantenimientos: historialData.mantenimientos || [],
        citasFuturas: historialData.citasFuturas || []
      };
    }
    
    return {
      mantenimientos: [],
      citasFuturas: []
    };
  } catch (error) {
    console.error(`❌ [MantenimientosService] Error obteniendo historial para vehículo ${patente}:`, error);
    throw error;
  }
};

// Aliases para compatibilidad con el código existente
export const obtenerMantenimientos = getMantenimientos;
export const obtenerMantenimientoPorId = getMantenimientoById;
export const crearMantenimiento = createMantenimiento;
export const actualizarMantenimiento = updateMantenimiento;
export const eliminarMantenimiento = deleteMantenimiento;